// Client-side Harris corner detection on a downscaled copy of the plan image
const MAX_DIM = 1000;
const K = 0.04;
const MAX_CORNERS = 500;

export async function detectCorners(image, imageDims) {
  const w0 = imageDims.width, h0 = imageDims.height;
  const s = Math.min(1, MAX_DIM / Math.max(w0, h0));
  const w = Math.max(2, Math.round(w0 * s));
  const h = Math.max(2, Math.round(h0 * s));

  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(image, 0, 0, w, h);
  let data;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch (e) {
    return []; // tainted canvas (cross-origin without CORS)
  }

  const gray = new Float32Array(w * h);
  for (let i = 0, j = 0; i < gray.length; i++, j += 4) {
    gray[i] = (data[j] * 0.299 + data[j + 1] * 0.587 + data[j + 2] * 0.114) / 255;
  }

  // Sobel gradients and structure tensor products
  const ixx = new Float32Array(w * h), iyy = new Float32Array(w * h), ixy = new Float32Array(w * h);
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const gx = -gray[i - w - 1] + gray[i - w + 1] - 2 * gray[i - 1] + 2 * gray[i + 1] - gray[i + w - 1] + gray[i + w + 1];
      const gy = -gray[i - w - 1] - 2 * gray[i - w] - gray[i - w + 1] + gray[i + w - 1] + 2 * gray[i + w] + gray[i + w + 1];
      ixx[i] = gx * gx; iyy[i] = gy * gy; ixy[i] = gx * gy;
    }
  }
  const sxx = boxBlur(ixx, w, h, 2), syy = boxBlur(iyy, w, h, 2), sxy = boxBlur(ixy, w, h, 2);

  // Harris response
  const R = new Float32Array(w * h);
  let maxR = 0;
  for (let i = 0; i < R.length; i++) {
    const det = sxx[i] * syy[i] - sxy[i] * sxy[i];
    const tr = sxx[i] + syy[i];
    const r = det - K * tr * tr;
    R[i] = r;
    if (r > maxR) maxR = r;
  }
  if (maxR <= 0) return [];

  // Threshold + non-maximum suppression
  const thresh = maxR * 0.01;
  const nms = 4;
  const found = [];
  for (let y = nms; y < h - nms; y++) {
    for (let x = nms; x < w - nms; x++) {
      const i = y * w + x;
      const r = R[i];
      if (r < thresh) continue;
      let isMax = true;
      for (let dy = -nms; dy <= nms && isMax; dy++) {
        for (let dx = -nms; dx <= nms; dx++) {
          if ((dx || dy) && R[i + dy * w + dx] > r) { isMax = false; break; }
        }
      }
      if (isMax) found.push({ x, y, r });
    }
  }
  found.sort((a, b) => b.r - a.r);
  return found.slice(0, MAX_CORNERS).map((c) => ({ x: (c.x + 0.5) / s, y: (c.y + 0.5) / s, strength: c.r / maxR }));
}

function boxBlur(src, w, h, radius) {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  const win = radius * 2 + 1;
  for (let y = 0; y < h; y++) {
    let sum = 0;
    const row = y * w;
    for (let x = -radius; x <= radius; x++) sum += src[row + clamp(x, 0, w - 1)];
    for (let x = 0; x < w; x++) {
      tmp[row + x] = sum / win;
      sum += src[row + clamp(x + radius + 1, 0, w - 1)] - src[row + clamp(x - radius, 0, w - 1)];
    }
  }
  for (let x = 0; x < w; x++) {
    let sum = 0;
    for (let y = -radius; y <= radius; y++) sum += tmp[clamp(y, 0, h - 1) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = sum / win;
      sum += tmp[clamp(y + radius + 1, 0, h - 1) * w + x] - tmp[clamp(y - radius, 0, h - 1) * w + x];
    }
  }
  return out;
}

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// Snap a point to the nearest detected corner within radius; otherwise axis-align with the reference point.
// Returns { point, kind: 'corner' | 'axis' | null, target }
export function snapPoint(p, corners, radius, reference, axisTol) {
  let best = null, bestD = radius * radius;
  for (const c of corners) {
    const dx = c.x - p.x, dy = c.y - p.y;
    const d = dx * dx + dy * dy;
    if (d < bestD) { bestD = d; best = c; }
  }
  if (best) return { point: { x: best.x, y: best.y }, kind: "corner", target: best };
  if (reference && axisTol > 0) {
    const out = { ...p };
    let snapped = false;
    if (Math.abs(p.x - reference.x) < axisTol) { out.x = reference.x; snapped = true; }
    if (Math.abs(p.y - reference.y) < axisTol) { out.y = reference.y; snapped = true; }
    if (snapped) return { point: out, kind: "axis", target: reference };
  }
  return { point: p, kind: null, target: null };
}
